import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useObserver } from 'mobx-react';

import { CategoryOptions } from 'shared/constants';
import Product from 'components/product';
import EmptyLoadingState from 'src/components/empty-loading-state';
import { Container } from 'components/product/layout';
import BackToMenu from 'components/product/back-to-menu';
import { DispensaryProductLayout } from 'src/components/layouts/dispensary-product-layout';
import { OftenPurchasedWithCarousel, RelatedItemsCarousel } from 'src/components/carousels';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useProduct from 'hooks/use-product';
import { isBrowser } from 'utils/misc-utils';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import useStores from 'shared/hooks/use-stores';
import { getEventDescription } from 'src/utils/helpers/product';
import { WhoopsContent, WhoopsReason } from 'src/components/whoops';

export default function DispensaryProductPage() {
  const { dispensary } = useDispensary();
  const { product, loading } = useProduct();
  const { UI } = useStores();
  const productPosition = useObserver(() => UI.productPosition);
  const carouselLabels = useObserver(() => UI.carouselLabels);
  const category = useMemo(() => (product ? _.find(CategoryOptions, { value: product.type }) : {}), [product]);
  const amplitude = useAmplitude();

  useEffect(() => {
    if (!_.isEmpty(category?.value) && !_.isEmpty(dispensary?.id) && !_.isEmpty(product?.id)) {
      const eventDescription = getEventDescription(productPosition, carouselLabels, {
        categoryName: category.value,
        dispensaryId: dispensary.id,
        dispensaryName: dispensary?.name,
        productId: product.id,
        productName: product?.name,
        menuVariant: UI.analyticsMenuVariant,
      });
      amplitude.log(AmplitudeEvents.menu.viewProduct, eventDescription);
    }
    // rationale: exhaustive deps cause these events to be fired more than we want
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  if (!dispensary) {
    return null;
  }

  if (!loading && !product) {
    // we finished loading but still don't have a product
    // effectively a 404
    if (isBrowser()) {
      return <WhoopsContent reason={WhoopsReason.pageNotFound} />;
    }
    return null;
  }

  return (
    <Container key={product?.id}>
      <BackToMenu category={category} />
      {/* eslint-disable-next-line i18next/no-literal-string */}
      <EmptyLoadingState isLoading={loading || !product} page='product'>
        <Product product={product} />
      </EmptyLoadingState>
      <RelatedItemsCarousel productId={product?.id} category={product?.type} />
      {product && <OftenPurchasedWithCarousel productId={product.id} category={product.type} />}
    </Container>
  );
}

DispensaryProductPage.layout = DispensaryProductLayout;
